import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Badge, Tab, Tabs, Table } from 'react-bootstrap';
import { Clock, Plus, Calendar, BarChart, PlayFill, StopFill } from 'react-bootstrap-icons';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const TimeTracking = ({ activeProfileId }) => {
    const [timetables, setTimetables] = useState([]);
    const [timeLogs, setTimeLogs] = useState([]);
    const [timeAnalysis, setTimeAnalysis] = useState(null);
    const [activeTimetable, setActiveTimetable] = useState(null);
    const [timetableSlots, setTimetableSlots] = useState([]);
    const [showTimetableModal, setShowTimetableModal] = useState(false);
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [activeTab, setActiveTab] = useState('timetable');
    const [isTracking, setIsTracking] = useState(false);
    const [currentActivity, setCurrentActivity] = useState(null);

    const [timetableForm, setTimetableForm] = useState({
        name: '',
        description: ''
    });

    const [slotForm, setSlotForm] = useState({
        activity_name: '',
        day_of_week: 0,
        start_time: '',
        end_time: '',
        category: '',
        color: '#3498db',
        apply_to_days: [0] // Array of selected days
    });

    const [logForm, setLogForm] = useState({
        activity_name: '',
        category: '',
        start_time: '',
        end_time: '',
        notes: ''
    });

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const categories = ['Work', 'Study', 'Exercise', 'Personal', 'Social', 'Entertainment', 'Other'];

    useEffect(() => {
        if (activeProfileId) {
            fetchTimetables();
            fetchTimeLogs();
            fetchTimeAnalysis();
        }
    }, [activeProfileId]);

    const fetchTimetables = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/timetables`);
            setTimetables(response.data);
            if (response.data.length > 0 && !activeTimetable) {
                setActiveTimetable(response.data[0]);
                fetchTimetableSlots(response.data[0].id);
            }
        } catch (error) {
            console.error('Error fetching timetables:', error);
        }
    };

    const fetchTimetableSlots = async (timetableId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/timetables/${timetableId}/slots`);
            console.log('Fetched slots:', response.data); // Debug log
            setTimetableSlots(response.data);
        } catch (error) {
            console.error('Error fetching timetable slots:', error);
        }
    };

    const fetchTimeLogs = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/time-logs`);
            setTimeLogs(response.data);
        } catch (error) {
            console.error('Error fetching time logs:', error);
        }
    };

    const fetchTimeAnalysis = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/time-analysis`);
            setTimeAnalysis(response.data);
        } catch (error) {
            console.error('Error fetching time analysis:', error);
        }
    };

    const handleCreateTimetable = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', timetableForm.name);
        formData.append('description', timetableForm.description);

        try {
            await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/timetables`, formData);
            fetchTimetables();
            setShowTimetableModal(false);
            setTimetableForm({ name: '', description: '' });
        } catch (error) {
            console.error('Error creating timetable:', error);
        }
    };

    const handleCreateSlot = async (e) => {
        e.preventDefault();
        
        try {
            if (selectedSlot) {
                // Edit existing slot
                const formData = new FormData();
                formData.append('activity_name', slotForm.activity_name);
                formData.append('day_of_week', slotForm.day_of_week);
                formData.append('start_time', slotForm.start_time);
                formData.append('end_time', slotForm.end_time);
                formData.append('category', slotForm.category);
                formData.append('color', slotForm.color);
                await axios.put(`${API_BASE_URL}/api/life/timetable-slots/${selectedSlot.id}`, formData);
            } else {
                // Create new slots for selected days
                const promises = slotForm.apply_to_days.map(dayIndex => {
                    const formData = new FormData();
                    formData.append('activity_name', slotForm.activity_name);
                    formData.append('day_of_week', dayIndex);
                    formData.append('start_time', slotForm.start_time);
                    formData.append('end_time', slotForm.end_time);
                    formData.append('category', slotForm.category);
                    formData.append('color', slotForm.color);
                    return axios.post(`${API_BASE_URL}/api/life/timetables/${activeTimetable.id}/slots`, formData);
                });
                await Promise.all(promises);
            }
            
            fetchTimetableSlots(activeTimetable.id);
            setShowSlotModal(false);
            setSelectedSlot(null);
            setSlotForm({
                activity_name: '',
                day_of_week: 0,
                start_time: '',
                end_time: '',
                category: '',
                color: '#3498db',
                apply_to_days: [0]
            });
        } catch (error) {
            console.error('Error saving slot:', error);
        }
    };

    const handleEditSlot = (slot) => {
        setSelectedSlot(slot);
        setSlotForm({
            activity_name: slot.activity_name,
            day_of_week: slot.day_of_week,
            start_time: slot.start_time,
            end_time: slot.end_time,
            category: slot.category || '',
            color: slot.color || '#3498db',
            apply_to_days: [slot.day_of_week]
        });
        setShowSlotModal(true);
    };

    const handleDeleteSlot = async (slotId) => {
        if (window.confirm('Are you sure you want to delete this time slot?')) {
            try {
                await axios.delete(`${API_BASE_URL}/api/life/timetable-slots/${slotId}`);
                fetchTimetableSlots(activeTimetable.id);
                setShowSlotModal(false);
                setSelectedSlot(null);
            } catch (error) {
                console.error('Error deleting slot:', error);
            }
        }
    };

    const handleAddSlot = () => {
        setSelectedSlot(null);
        setSlotForm({
            activity_name: '',
            day_of_week: 0,
            start_time: '',
            end_time: '',
            category: '',
            color: '#3498db',
            apply_to_days: [0]
        });
        setShowSlotModal(true);
    };

    const handleCreateTimeLog = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        Object.keys(logForm).forEach(key => {
            if (logForm[key]) formData.append(key, logForm[key]);
        });

        try {
            if (selectedLog) {
                await axios.put(`${API_BASE_URL}/api/life/time-logs/${selectedLog.id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/time-logs`, formData);
            }
            fetchTimeLogs();
            fetchTimeAnalysis();
            setShowLogModal(false);
            setSelectedLog(null);
            setLogForm({
                activity_name: '',
                category: '',
                start_time: '',
                end_time: '',
                notes: ''
            });
        } catch (error) {
            console.error('Error saving time log:', error);
        }
    };

    const handleEditLog = (log) => {
        setSelectedLog(log);
        const startTime = new Date(log.start_time).toISOString().slice(0, 16);
        const endTime = log.end_time ? new Date(log.end_time).toISOString().slice(0, 16) : '';
        
        setLogForm({
            activity_name: log.activity_name,
            category: log.category || '',
            start_time: startTime,
            end_time: endTime,
            notes: log.notes || ''
        });
        setShowLogModal(true);
    };

    const handleDeleteLog = async (logId) => {
        if (window.confirm('Are you sure you want to delete this time log?')) {
            try {
                await axios.delete(`${API_BASE_URL}/api/life/time-logs/${logId}`);
                fetchTimeLogs();
                fetchTimeAnalysis();
            } catch (error) {
                console.error('Error deleting time log:', error);
            }
        }
    };

    const startTracking = (activityName, category = '') => {
        const now = new Date().toISOString();
        setCurrentActivity({ activity_name: activityName, category, start_time: now });
        setIsTracking(true);
    };

    const stopTracking = async () => {
        if (!currentActivity) return;
        
        const now = new Date().toISOString();
        const formData = new FormData();
        formData.append('activity_name', currentActivity.activity_name);
        formData.append('category', currentActivity.category);
        formData.append('start_time', currentActivity.start_time);
        formData.append('end_time', now);

        try {
            await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/time-logs`, formData);
            fetchTimeLogs();
            fetchTimeAnalysis();
            setIsTracking(false);
            setCurrentActivity(null);
        } catch (error) {
            console.error('Error stopping tracking:', error);
        }
    };

    const renderTimetableGrid = () => {
        console.log('Rendering grid with slots:', timetableSlots); // Debug
        
        const timeSlots = [];
        for (let hour = 0; hour < 24; hour++) {
            timeSlots.push({ hour, minute: 0, display: `${hour.toString().padStart(2, '0')}:00` });
            timeSlots.push({ hour, minute: 30, display: `${hour.toString().padStart(2, '0')}:30` });
        }
        
        return (
            <div className="timetable-grid" style={{ overflowX: 'auto' }}>
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th style={{ minWidth: '80px' }}>Time</th>
                            {dayNames.map((day, index) => (
                                <th key={index} style={{ minWidth: '120px' }}>{day}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {timeSlots.map((timeSlot, index) => (
                            <tr key={index} style={{ height: '30px' }}>
                                <td style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                    {timeSlot.display}
                                </td>
                                {dayNames.map((_, dayIndex) => {
                                    // Find any slot for this day and time
                                    const slotForCell = timetableSlots.find(slot => {
                                        const slotDay = parseInt(slot.day_of_week);
                                        const [startHour, startMin] = slot.start_time.split(':').map(Number);
                                        const [endHour, endMin] = slot.end_time.split(':').map(Number);
                                        
                                        if (slotDay !== dayIndex) return false;
                                        
                                        const currentTime = timeSlot.hour + timeSlot.minute / 60;
                                        const startTime = startHour + (startMin || 0) / 60;
                                        const endTime = endHour + (endMin || 0) / 60;
                                        
                                        // Handle overnight slots (e.g., 22:00 to 05:00)
                                        if (endTime < startTime) {
                                            return currentTime >= startTime || currentTime < endTime;
                                        } else {
                                            return currentTime >= startTime && currentTime < endTime;
                                        }
                                    });
                                    
                                    return (
                                        <td key={dayIndex} style={{ 
                                            backgroundColor: slotForCell ? slotForCell.color : '#f8f9fa',
                                            color: slotForCell ? 'white' : 'black',
                                            fontSize: '0.7rem',
                                            padding: '4px',
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                            border: '1px solid #dee2e6',
                                            minHeight: '30px'
                                        }}>
                                            {slotForCell ? (
                                                <div 
                                                    onClick={() => handleEditSlot(slotForCell)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to edit"
                                                >
                                                    <strong style={{ fontSize: '0.65rem' }}>{slotForCell.activity_name}</strong>
                                                    {slotForCell.category && (
                                                        <>
                                                            <br />
                                                            <small style={{ fontSize: '0.6rem' }}>{slotForCell.category}</small>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#ccc' }}>-</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="time-tracking-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5><Clock className="me-2" />Time Tracking</h5>
                {isTracking ? (
                    <Button variant="danger" onClick={stopTracking}>
                        <StopFill className="me-1" />
                        Stop: {currentActivity?.activity_name}
                    </Button>
                ) : (
                    <Button variant="success" onClick={() => setShowLogModal(true)}>
                        <PlayFill className="me-1" />
                        Start Tracking
                    </Button>
                )}
            </div>

            <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                <Tab eventKey="timetable" title="Timetable">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            {timetables.length > 0 && (
                                <Form.Select 
                                    value={activeTimetable?.id || ''} 
                                    onChange={(e) => {
                                        const selected = timetables.find(t => t.id === parseInt(e.target.value));
                                        setActiveTimetable(selected);
                                        if (selected) fetchTimetableSlots(selected.id);
                                    }}
                                    style={{ width: '200px' }}
                                >
                                    {timetables.map(timetable => (
                                        <option key={timetable.id} value={timetable.id}>
                                            {timetable.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </div>
                        <div>
                            <Button variant="outline-primary" className="me-2" onClick={() => setShowTimetableModal(true)}>
                                <Plus /> New Timetable
                            </Button>
                            {activeTimetable && (
                                <Button variant="primary" onClick={handleAddSlot}>
                                    <Plus /> Add Slot
                                </Button>
                            )}
                        </div>
                    </div>

                    {activeTimetable ? (
                        <>
                            {renderTimetableGrid()}
                        </>
                    ) : (
                        <Card className="text-center p-4">
                            <p className="text-muted">Create a timetable to start scheduling your activities</p>
                        </Card>
                    )}
                </Tab>

                <Tab eventKey="logs" title="Time Logs">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>Recent Activities</h6>
                        <Button variant="primary" onClick={() => setShowLogModal(true)}>
                            <Plus /> Log Time
                        </Button>
                    </div>

                    {timeLogs.length === 0 ? (
                        <Card className="text-center p-4">
                            <p className="text-muted">No time logs yet. Start tracking your activities!</p>
                        </Card>
                    ) : (
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Activity</th>
                                    <th>Category</th>
                                    <th>Duration</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeLogs.slice(0, 10).map(log => (
                                    <tr key={log.id}>
                                        <td>{log.activity_name}</td>
                                        <td>
                                            {log.category && <Badge bg="secondary">{log.category}</Badge>}
                                        </td>
                                        <td>
                                            {log.duration_minutes ? `${log.duration_minutes} min` : 'In progress'}
                                        </td>
                                        <td>{new Date(log.start_time).toLocaleDateString()}</td>
                                        <td>
                                            <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEditLog(log)}>
                                                Edit
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteLog(log.id)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Tab>

                <Tab eventKey="analysis" title="Analysis">
                    {timeAnalysis ? (
                        <Row>
                            <Col md={6}>
                                <Card className="mb-3">
                                    <Card.Header>Category Breakdown</Card.Header>
                                    <Card.Body>
                                        {Object.entries(timeAnalysis.category_analysis).map(([category, data]) => (
                                            <div key={category} className="d-flex justify-content-between mb-2">
                                                <span>{category}</span>
                                                <Badge bg="primary">{Math.round(data.total_minutes / 60 * 10) / 10}h</Badge>
                                            </div>
                                        ))}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card className="mb-3">
                                    <Card.Header>Top Activities</Card.Header>
                                    <Card.Body>
                                        {Object.entries(timeAnalysis.activity_analysis)
                                            .sort(([,a], [,b]) => b.total_minutes - a.total_minutes)
                                            .slice(0, 5)
                                            .map(([activity, data]) => (
                                                <div key={activity} className="d-flex justify-content-between mb-2">
                                                    <span>{activity}</span>
                                                    <Badge bg="success">{Math.round(data.total_minutes / 60 * 10) / 10}h</Badge>
                                                </div>
                                            ))}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={12}>
                                <Card className="mb-3">
                                    <Card.Header>Planned vs Actual Time Comparison</Card.Header>
                                    <Card.Body>
                                        {activeTimetable && timetableSlots.length > 0 ? (
                                            <Table striped bordered hover size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Activity</th>
                                                        <th>Planned (Weekly)</th>
                                                        <th>Actual (This Week)</th>
                                                        <th>Difference</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        // Calculate planned time from timetable
                                                        const plannedTime = {};
                                                        timetableSlots.forEach(slot => {
                                                            const [startHour, startMin] = slot.start_time.split(':').map(Number);
                                                            const [endHour, endMin] = slot.end_time.split(':').map(Number);
                                                            let duration;
                                                            
                                                            if (endHour < startHour) {
                                                                // Overnight slot
                                                                duration = (24 - startHour - startMin/60) + (endHour + endMin/60);
                                                            } else {
                                                                duration = (endHour + endMin/60) - (startHour + startMin/60);
                                                            }
                                                            
                                                            if (!plannedTime[slot.activity_name]) {
                                                                plannedTime[slot.activity_name] = 0;
                                                            }
                                                            plannedTime[slot.activity_name] += duration;
                                                        });

                                                        // Get actual time from logs (last 7 days)
                                                        const weekAgo = new Date();
                                                        weekAgo.setDate(weekAgo.getDate() - 7);
                                                        
                                                        const recentLogs = timeLogs.filter(log => 
                                                            new Date(log.start_time) >= weekAgo && log.duration_minutes
                                                        );
                                                        
                                                        const actualTime = {};
                                                        recentLogs.forEach(log => {
                                                            if (!actualTime[log.activity_name]) {
                                                                actualTime[log.activity_name] = 0;
                                                            }
                                                            actualTime[log.activity_name] += log.duration_minutes / 60;
                                                        });

                                                        // Combine planned and actual
                                                        const allActivities = new Set([...Object.keys(plannedTime), ...Object.keys(actualTime)]);
                                                        
                                                        return Array.from(allActivities).map(activity => {
                                                            const planned = plannedTime[activity] || 0;
                                                            const actual = actualTime[activity] || 0;
                                                            const difference = actual - planned;
                                                            const percentage = planned > 0 ? (actual / planned * 100) : 0;
                                                            
                                                            let status = 'On Track';
                                                            let statusColor = 'success';
                                                            
                                                            if (percentage < 80) {
                                                                status = 'Under';
                                                                statusColor = 'warning';
                                                            } else if (percentage > 120) {
                                                                status = 'Over';
                                                                statusColor = 'info';
                                                            }
                                                            
                                                            return (
                                                                <tr key={activity}>
                                                                    <td>{activity}</td>
                                                                    <td>{planned.toFixed(1)}h</td>
                                                                    <td>{actual.toFixed(1)}h</td>
                                                                    <td style={{ color: difference >= 0 ? 'green' : 'red' }}>
                                                                        {difference >= 0 ? '+' : ''}{difference.toFixed(1)}h
                                                                    </td>
                                                                    <td>
                                                                        <Badge bg={statusColor}>{status}</Badge>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <p className="text-muted">Create a timetable with slots to see planned vs actual comparison</p>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        <Card className="text-center p-4">
                            <p className="text-muted">No data available for analysis</p>
                        </Card>
                    )}
                </Tab>
            </Tabs>

            {/* Timetable Modal */}
            <Modal show={showTimetableModal} onHide={() => setShowTimetableModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create Timetable</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateTimetable}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control 
                                value={timetableForm.name}
                                onChange={(e) => setTimetableForm({...timetableForm, name: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control 
                                as="textarea"
                                rows={3}
                                value={timetableForm.description}
                                onChange={(e) => setTimetableForm({...timetableForm, description: e.target.value})}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowTimetableModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit">Create</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Slot Modal */}
            <Modal show={showSlotModal} onHide={() => {setShowSlotModal(false); setSelectedSlot(null);}}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedSlot ? 'Edit Time Slot' : 'Add Time Slot'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateSlot}>
                        <Form.Group className="mb-3">
                            <Form.Label>Activity Name *</Form.Label>
                            <Form.Control 
                                value={slotForm.activity_name}
                                onChange={(e) => setSlotForm({...slotForm, activity_name: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{selectedSlot ? 'Day' : 'Apply to Days'}</Form.Label>
                                    {selectedSlot ? (
                                        <Form.Select 
                                            value={slotForm.day_of_week}
                                            onChange={(e) => setSlotForm({...slotForm, day_of_week: parseInt(e.target.value)})}
                                        >
                                            {dayNames.map((day, index) => (
                                                <option key={index} value={index}>{day}</option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        <div>
                                            <div className="mb-2">
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Select All Days"
                                                    checked={slotForm.apply_to_days.length === 7}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSlotForm({...slotForm, apply_to_days: [0,1,2,3,4,5,6]});
                                                        } else {
                                                            setSlotForm({...slotForm, apply_to_days: [0]});
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {dayNames.map((day, index) => (
                                                <Form.Check
                                                    key={index}
                                                    type="checkbox"
                                                    label={day}
                                                    checked={slotForm.apply_to_days.includes(index)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSlotForm({
                                                                ...slotForm, 
                                                                apply_to_days: [...slotForm.apply_to_days, index]
                                                            });
                                                        } else {
                                                            setSlotForm({
                                                                ...slotForm, 
                                                                apply_to_days: slotForm.apply_to_days.filter(d => d !== index)
                                                            });
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Category</Form.Label>
                                    <Form.Select 
                                        value={slotForm.category}
                                        onChange={(e) => setSlotForm({...slotForm, category: e.target.value})}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Time *</Form.Label>
                                    <Form.Control 
                                        type="time"
                                        value={slotForm.start_time}
                                        onChange={(e) => setSlotForm({...slotForm, start_time: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Time *</Form.Label>
                                    <Form.Control 
                                        type="time"
                                        value={slotForm.end_time}
                                        onChange={(e) => setSlotForm({...slotForm, end_time: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Color</Form.Label>
                            <Form.Control 
                                type="color"
                                value={slotForm.color}
                                onChange={(e) => setSlotForm({...slotForm, color: e.target.value})}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-between">
                            <div>
                                {selectedSlot && (
                                    <Button variant="danger" onClick={() => handleDeleteSlot(selectedSlot.id)}>
                                        Delete Slot
                                    </Button>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="secondary" onClick={() => {setShowSlotModal(false); setSelectedSlot(null);}}>Cancel</Button>
                                <Button variant="primary" type="submit">{selectedSlot ? 'Update Slot' : 'Add Slot'}</Button>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Time Log Modal */}
            <Modal show={showLogModal} onHide={() => {setShowLogModal(false); setSelectedLog(null);}}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedLog ? 'Edit Time Log' : 'Log Time'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateTimeLog}>
                        <Form.Group className="mb-3">
                            <Form.Label>Activity Name *</Form.Label>
                            <Form.Control 
                                value={logForm.activity_name}
                                onChange={(e) => setLogForm({...logForm, activity_name: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select 
                                value={logForm.category}
                                onChange={(e) => setLogForm({...logForm, category: e.target.value})}
                            >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Time *</Form.Label>
                                    <Form.Control 
                                        type="datetime-local"
                                        value={logForm.start_time}
                                        onChange={(e) => setLogForm({...logForm, start_time: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Time</Form.Label>
                                    <Form.Control 
                                        type="datetime-local"
                                        value={logForm.end_time}
                                        onChange={(e) => setLogForm({...logForm, end_time: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control 
                                as="textarea"
                                rows={3}
                                value={logForm.notes}
                                onChange={(e) => setLogForm({...logForm, notes: e.target.value})}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowLogModal(false); setSelectedLog(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedLog ? 'Update Log' : 'Log Time'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default TimeTracking;
